import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Zap, X, Clock } from "lucide-react";

/*  Sitewide promo banner — pulls config from /api/packs/promo so copy and
    dates live on the backend. Three phases:
      - `preview`: pre-window teaser with countdown
      - `active`:  live discount
      - null:      hidden
    Dismissible — persists in localStorage so a user who dismisses once
    doesn't see it again until the next phase change.
*/

const DISMISS_KEY = "wm_promo_dismissed_v1";

const fmtCountdown = (targetIso) => {
  const t = new Date(targetIso).getTime();
  const diff = t - Date.now();
  if (diff <= 0) return "now";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  if (d >= 1) return `in ${d}d ${h}h`;
  const m = Math.floor((diff % 3600000) / 60000);
  return `in ${h}h ${m}m`;
};

export const PromoBanner = () => {
  const [promo, setPromo] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) || ""; } catch { return ""; }
  });
  const [tick, setTick] = useState(0);  // forces countdown re-render

  useEffect(() => {
    api.get("/packs/promo").then((r) => setPromo(r.data?.promo || null)).catch(() => {});
  }, []);

  // Re-render once a minute so the countdown stays accurate
  useEffect(() => {
    if (!promo) return;
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, [promo]);

  if (!promo) return null;
  // Per-phase dismiss key — if user dismissed the "preview" phase, they'll
  // still see the "active" banner when the real sale starts
  const myKey = `${promo.id}:${promo.phase}`;
  if (dismissedKey === myKey) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, myKey); } catch {}
    setDismissedKey(myKey);
  };

  const countdownTarget = promo.phase === "preview" ? promo.starts_at : promo.ends_at;
  const countdownLabel  = promo.phase === "preview" ? "OPENS" : "ENDS";

  return (
    <div className="relative z-[60]" data-testid="promo-banner" data-phase={promo.phase}>
      <div className={`${
        promo.phase === "active"
          ? "bg-gradient-to-r from-[#8b0000] via-[#d4af37] to-[#8b0000] text-black"
          : "bg-[#0a0908] border-b border-[#d4af37]/40 text-[#f4f0ec]"
      } px-4 py-2.5`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/packs" className="flex-1 flex items-center gap-2 min-w-0 group"
            data-testid="promo-banner-link">
            <Zap size={14} className={`${promo.phase === "active" ? "text-black" : "text-[#d4af37]"} flex-shrink-0`} />
            <span className="text-[10px] sm:text-xs tracking-[0.25em] font-bold truncate">
              {promo.label}
            </span>
            <span className="hidden sm:inline opacity-60">·</span>
            <span className="hidden sm:inline text-[10px] sm:text-xs tracking-wider opacity-90 truncate">
              {promo.subline}
            </span>
            <span className={`text-[10px] tracking-widest ml-auto hidden md:flex items-center gap-1 ${
              promo.phase === "active" ? "opacity-80" : "text-[#d4af37]"
            }`}>
              <Clock size={10} /> {countdownLabel} {fmtCountdown(countdownTarget)}
            </span>
            <span className="text-[10px] tracking-widest ml-auto md:hidden">
              {promo.cta}
            </span>
          </Link>
          <button onClick={dismiss}
            className={`${
              promo.phase === "active" ? "text-black/60 hover:text-black" : "text-[#5a5050] hover:text-[#f4f0ec]"
            } flex-shrink-0`}
            aria-label="Dismiss promo banner"
            data-testid="promo-banner-dismiss">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
